import React, { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"

import Footer from "../components/common/Footer"
//aliased to a PascalCase name so the jsx naming lint rule stays quiet
import CourseCard from "../components/core/Catalog/Course_Card"
import { getAllCourses } from "../services/operations/courseDetailsAPI"
import { fetchCategories } from "../services/operations/adminAPI"

const SORT_CHOICES = [
  { value: "newest", label: "Newest" },
  { value: "popular", label: "Most popular" },
  { value: "price-low", label: "Price: low to high" },
  { value: "price-high", label: "Price: high to low" },
]

const PAGE_SIZE = 12

//Browse every published course, rather than one category at a time like the
//catalog pages do. The filters live in the url so a search can be shared or
//survive a refresh.
const Courses = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [courses, setCourses] = useState([])
  const [pagination, setPagination] = useState(null)
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)

  //The search box is kept separate from the applied filter so that typing does
  //not fire a request per keystroke...it is applied on submit
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "")

  const search = searchParams.get("search") || ""
  const category = searchParams.get("category") || ""
  const minPrice = searchParams.get("minPrice") || ""
  const maxPrice = searchParams.get("maxPrice") || ""
  const sort = searchParams.get("sort") || "newest"
  const page = parseInt(searchParams.get("page"), 10) || 1

  useEffect(() => {
    ;(async () => {
      const result = await fetchCategories()
      setCategories(result)
    })()
  }, [])

  useEffect(() => {
    ;(async () => {
      setLoading(true)

      const result = await getAllCourses({
        search,
        category,
        minPrice,
        maxPrice,
        sort,
        page,
        limit: PAGE_SIZE,
      })

      setCourses(result.courses)
      setPagination(result.pagination)
      setLoading(false)
    })()
  }, [search, category, minPrice, maxPrice, sort, page])

  //Any filter change sends the user back to page one, otherwise they can land on
  //a page that no longer exists in the narrowed results
  const updateFilter = (changes) => {
    const next = Object.fromEntries(searchParams.entries())

    Object.entries(changes).forEach(([key, value]) => {
      if (value === "" || value === null || value === undefined) {
        delete next[key]
      } else {
        next[key] = value
      }
    })

    delete next.page
    setSearchParams(next)
  }

  const goToPage = (nextPage) => {
    const next = Object.fromEntries(searchParams.entries())
    next.page = nextPage
    setSearchParams(next)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const clearFilters = () => {
    setSearchInput("")
    setSearchParams({})
  }

  const hasFilters = search || category || minPrice || maxPrice || sort !== "newest"

  return (
    <>
      <div className="mx-auto w-11/12 max-w-maxContent py-12">
        <h1 className="text-3xl font-medium text-richblack-5">All Courses</h1>

        <p className="mt-2 text-richblack-300">
          Search and filter across everything on the platform
        </p>

        {/* search */}
        <form
          onSubmit={(event) => {
            event.preventDefault()
            updateFilter({ search: searchInput.trim() })
          }}
          className="mt-8 flex flex-col gap-3 sm:flex-row"
        >
          <input
            type="text"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search courses by name, description or tag"
            className="w-full rounded-md bg-richblack-700 p-3 text-richblack-5 placeholder:text-richblack-400"
          />

          <button
            type="submit"
            className="shrink-0 rounded-md bg-yellow-50 px-6 py-3 font-semibold text-richblack-900"
          >
            Search
          </button>
        </form>

        {/* filters */}
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <select
            value={category}
            onChange={(event) => updateFilter({ category: event.target.value })}
            className="rounded-md bg-richblack-700 p-3 text-richblack-5"
          >
            <option value="">All categories</option>
            {categories.map((item) => (
              <option key={item._id} value={item._id}>
                {item.name}
              </option>
            ))}
          </select>

          <select
            value={sort}
            onChange={(event) => updateFilter({ sort: event.target.value })}
            className="rounded-md bg-richblack-700 p-3 text-richblack-5"
          >
            {SORT_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>

          <input
            type="number"
            min="0"
            value={minPrice}
            onChange={(event) => updateFilter({ minPrice: event.target.value })}
            placeholder="Min price"
            className="w-32 rounded-md bg-richblack-700 p-3 text-richblack-5 placeholder:text-richblack-400"
          />

          <input
            type="number"
            min="0"
            value={maxPrice}
            onChange={(event) => updateFilter({ maxPrice: event.target.value })}
            placeholder="Max price"
            className="w-32 rounded-md bg-richblack-700 p-3 text-richblack-5 placeholder:text-richblack-400"
          />

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="rounded-md bg-richblack-700 px-4 py-3 text-richblack-50"
            >
              Clear
            </button>
          )}
        </div>

        {/* results */}
        {loading ? (
          <div className="mt-16 flex justify-center">
            <div className="spinner"></div>
          </div>
        ) : courses.length === 0 ? (
          <p className="mt-20 text-center text-2xl text-richblack-100">
            No courses match these filters
          </p>
        ) : (
          <>
            <p className="mt-8 text-sm text-richblack-300">
              {pagination?.totalCourses} course
              {pagination?.totalCourses === 1 ? "" : "s"} found
            </p>

            <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {courses.map((course) => (
                <CourseCard
                  key={course._id}
                  course={course}
                  Height={"h-[250px]"}
                />
              ))}
            </div>

            {pagination?.totalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-4">
                <button
                  disabled={!pagination.hasPrevPage}
                  onClick={() => goToPage(page - 1)}
                  className="rounded-md bg-richblack-700 px-5 py-2 text-richblack-50 disabled:opacity-40"
                >
                  Previous
                </button>

                <span className="text-richblack-100">
                  Page {pagination.page} of {pagination.totalPages}
                </span>

                <button
                  disabled={!pagination.hasNextPage}
                  onClick={() => goToPage(page + 1)}
                  className="rounded-md bg-richblack-700 px-5 py-2 text-richblack-50 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </>
  )
}

export default Courses
